import EndpointCard from '../EndpointCard';
import ParamTable from '../ParamTable';
import CodeBlock from '../CodeBlock';

export default function PaymentSection() {
  return (
    <>
      <div className="mt-12 mb-8">
        <p className="font-mono text-[10px] text-bone-white/30 uppercase tracking-widest">Payments</p>
      </div>

      <EndpointCard
        id="create-checkout"
        method="POST"
        path="/api/checkout/create"
        description="Create a checkout session. 99% of the USDC payment goes directly to your wallet, 1% goes to BonesPay treasury. Funds are settled on-chain on Polygon."
        auth
      />
      <ParamTable
        title="Request Body"
        params={[
          { field: 'merchant_wallet', type: 'string', required: true, description: 'Polygon wallet address to receive 99% of payment' },
          { field: 'order_amount_usd', type: 'number', required: true, description: 'Payment amount in USD (must be positive)' },
          { field: 'order_currency', type: 'string', required: false, description: 'Original currency code (default: "USD")' },
          { field: 'order_original_amount', type: 'number', required: false, description: 'Amount in original currency before conversion' },
          { field: 'external_order_id', type: 'string', required: false, description: 'Your internal order ID for reconciliation' },
          { field: 'callback_url', type: 'string', required: false, description: 'Override webhook URL for this specific order' },
        ]}
      />
      <CodeBlock
        language="json"
        code={`{
  "merchant_wallet": "0x742d35Cc6634C0532925a3b844Bc9e7595f2bD18",
  "order_amount_usd": 49.99,
  "order_currency": "EUR",
  "order_original_amount": 45.50,
  "external_order_id": "WC-1234",
  "callback_url": "https://mystore.com/wc-api/bonespay"
}`}
      />

      <p className="font-mono text-[10px] text-bone-white/30 uppercase tracking-wider mt-6 mb-2">
        Response 201
      </p>
      <CodeBlock
        language="json"
        code={`{
  "success": true,
  "order_uuid": "d290f1ee-6c54-4b01-90e6-d701748f0851",
  "checkout_url": "https://commerce.moonpay.com/checkout/abc123",
  "callback_secret": "a1b2c3d4e5f6...",
  "expires_at": "2026-03-18T13:00:00.000Z"
}`}
      />
      <p className="font-sans text-xs text-bone-white/50 mt-3 leading-relaxed">
        Redirect the customer to checkout_url to complete payment. The session expires after 1 hour.
        Store the callback_secret to verify HMAC signatures on webhook callbacks.
      </p>

      <ParamTable
        title="Errors"
        params={[
          { field: 'X-Bonespay-Key header required', type: '401', description: 'Missing API key header' },
          { field: 'Invalid or inactive API key', type: '401', description: 'API key not found or merchant deactivated' },
          { field: 'Payment provider unavailable', type: '502', description: 'MoonPay Commerce could not create the session' },
        ]}
      />

      <div className="mt-12 mb-8">
        <p className="font-mono text-[10px] text-bone-white/30 uppercase tracking-widest">Orders</p>
      </div>

      <EndpointCard
        id="get-order"
        method="GET"
        path="/api/orders/:uuid"
        description="Fetch full order details including merchant branding. Used by the checkout page. No authentication required."
      />
      <CodeBlock
        language="json"
        code={`{
  "order_uuid": "d290f1ee-6c54-4b01-90e6-d701748f0851",
  "amount": 49.99,
  "original_amount": 45.50,
  "currency": "EUR",
  "checkout_url": "https://commerce.moonpay.com/checkout/abc123",
  "status": "pending",
  "merchant_branding": { "business_name": "My Store" },
  "created_at": "2026-03-18T12:00:00.000Z",
  "expires_at": "2026-03-18T13:00:00.000Z"
}`}
      />

      <EndpointCard
        id="order-status"
        method="GET"
        path="/api/orders/:uuid/status"
        description="Lightweight status polling endpoint. Returns only the current status string."
      />
      <CodeBlock language="json" code={`{ "status": "pending" }`} />

      <ParamTable
        title="Possible Status Values"
        params={[
          { field: 'pending', type: 'status', description: 'Checkout created, waiting for customer payment' },
          { field: 'completed', type: 'status', description: 'Payment confirmed on-chain, funds settled' },
          { field: 'failed', type: 'status', description: 'Payment failed' },
          { field: 'expired', type: 'status', description: 'Checkout session expired before payment' },
        ]}
      />
    </>
  );
}
